/**
 * API client for the per-simulation feature importer. Same thin-request idiom
 * as `src/features/tracker/api.ts`: shared `ApiError` + auth-header plumbing,
 * envelope unwrapping done here, callers get the payload.
 */

import { ApiError, errorFromResponse } from '@/lib/api';
import { getStoredToken, clearAuth } from '@/features/auth/utils/authStorage';

import type {
  FeatureImportConfig,
  FeatureImportConfigResponse,
  FeatureImportRun,
  FeatureImportRunResponse,
} from './types';

const API_BASE_URL = '/api';

function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function handleAuthError(): void {
  clearAuth();
  const path = window.location.pathname;
  if (path !== '/login' && path !== '/setup') {
    window.location.href = '/login';
  }
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  });
  if (response.status === 401) {
    handleAuthError();
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }
  if (response.status === 204) {
    return undefined as T;
  }
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  const text = await response.text();
  const json = JSON.parse(text) as { data: T };
  return json.data;
}

/**
 * Flatten the wire run (`result` nested, absent while running) into the
 * client view the UI renders — counts and warnings at the top level.
 */
function toRun(r: FeatureImportRunResponse): FeatureImportRun {
  const { result, ...rest } = r;
  return {
    ...rest,
    created: result?.created ?? 0,
    updated: result?.updated ?? 0,
    warnings: result?.warnings ?? [],
  };
}

/**
 * The stored feature-import config for a simulation, or `null` when none has
 * been saved yet (the endpoint 404s in that case — not an error for the UI,
 * it just means the Import affordance stays hidden). `null`, not `undefined`:
 * TanStack Query rejects `undefined` as query data.
 */
export async function getFeatureImportConfig(simulationId: string): Promise<FeatureImportConfigResponse | null> {
  try {
    const data = await request<FeatureImportConfigResponse | null>(
      `/tracker/simulations/${simulationId}/feature-import-config`,
    );
    return data ?? null;
  } catch (e) {
    if (e instanceof ApiError && e.statusCode === 404) return null;
    throw e;
  }
}

export async function putFeatureImportConfig(
  simulationId: string,
  config: FeatureImportConfig,
): Promise<FeatureImportConfigResponse> {
  return request<FeatureImportConfigResponse>(`/tracker/simulations/${simulationId}/feature-import-config`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

/** Trigger an import run — 202 Accepted with the freshly-inserted running run. */
export async function triggerFeatureImport(simulationId: string): Promise<FeatureImportRun> {
  const run = await request<FeatureImportRunResponse>(`/tracker/simulations/${simulationId}/feature-import`, {
    method: 'POST',
  });
  return toRun(run);
}

/**
 * The newest import run for a simulation, or `null` when none has run yet
 * (404 from the endpoint). `null`, not `undefined`: TanStack Query rejects
 * `undefined` as query data.
 */
export async function getLatestFeatureImport(simulationId: string): Promise<FeatureImportRun | null> {
  try {
    const run = await request<FeatureImportRunResponse | null>(
      `/tracker/simulations/${simulationId}/feature-imports/latest`,
    );
    return run ? toRun(run) : null;
  } catch (e) {
    if (e instanceof ApiError && e.statusCode === 404) return null;
    throw e;
  }
}
